/**
 * O painel de uma seção quando nenhum item foi escolhido — K-1 da rodada CASCA.
 *
 * ── Por que isto saiu da `ConfiguracoesPage`
 *
 * Enquanto a Configurações era um índice, a lista de itens podia morar dentro da
 * própria tela. Virando casca, o painel direito passa a ter **dois inquilinos**:
 * esta lista (sem item escolhido) e o conteúdo do item (com). Os dois não podem
 * estar no mesmo componente da moldura, senão a moldura remonta a cada troca — e
 * o menu piscar a cada clique é exatamente o que a casca existe pra evitar.
 *
 * A lista de seções e os helpers moram em `secoes.ts`, não aqui: hook e helper
 * convivendo com componente quebram o fast refresh. A moldura e este painel usam
 * o MESMO `useSecoesPermitidas` — duplicar o cálculo abriria a chance de o menu
 * mostrar uma seção que o painel considera inexistente.
 */
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { Card, EmptyState } from '@/components/ui';
import { type ItemConfig } from './catalogo';
import { comOrigem, useSecaoAtual } from './secoes';

/** Uma configuração na lista — nome, o que faz, e o caminho pra ela. */
export function LinhaItem({
  item,
  secao,
  secaoId,
}: {
  item: ItemConfig;
  secao?: string;
  secaoId: string;
}) {
  return (
    <Card className="min-w-0 p-0">
      <Link
        to={comOrigem(item.para, secaoId)}
        className="flex min-w-0 items-center gap-3 p-4 hover:bg-ink-1/5"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink-1">{item.nome}</span>
          <span className="block truncate text-xs text-ink-2">{item.desc}</span>
          {secao && <span className="mt-1 block text-xs text-ink-3">{secao}</span>}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
      </Link>
    </Card>
  );
}

export function SecaoIndice() {
  const atual = useSecaoAtual();

  if (!atual) {
    return (
      <EmptyState
        title="Nada para configurar aqui"
        description="Seu acesso não inclui nenhuma configuração. Se precisa de alguma, peça ao administrador."
        tamanho="compacto"
      />
    );
  }

  // K-4 (correção): SEM cabeçalho próprio, pelo mesmo motivo do `ItemPainel`.
  // O título e a descrição da seção já são desenhados pela casca — tê-los aqui
  // fazia "Pessoas e acesso" aparecer DUAS vezes em
  // `/admin/configuracoes/pessoas`. O `ItemPainel` perdeu o dele no K-4; este
  // ficou para trás, e o teste só olhava as 14 telas migradas, não a tela de
  // seção sem item.
  return (
    <>
      <ul className="flex flex-col gap-2">
        {atual.itens.map((i) => (
          <li key={i.para}>
            <LinhaItem item={i} secaoId={atual.id} />
          </li>
        ))}
      </ul>
    </>
  );
}
